import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faChevronDown, faChevronUp, faQuoteLeft, faClose, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import BoatReviewService from '../../services/BoatReviewService';
import { validateBoatReviewForm } from '../../validations/BoatReviewValidation';
import '../../styles/boat/BoatReviews.css';
import '../../styles/modal/CoreModal.css';

const BoatReviews = ({ boatId, boatName }) => {
  const [reviews, setReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [formData, setFormData] = useState({
    reviewer_name: '',
    email: '',
    comment: '',
    rating: 5,
    boat: boatId
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    loadReviews();
  }, [boatId]);

  const loadReviews = async () => {
    try {
      const reviewsData = await BoatReviewService.fetchBoatReviews(boatId);
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating: rating
    }));
    // Clear error when user changes rating
    if (errors.rating) {
      setErrors(prev => ({
        ...prev,
        rating: ''
      }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateBoatReviewForm(formData);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await BoatReviewService.createReview(formData);
      setShowSuccessMessage(true);
      setFormData({
        reviewer_name: '',
        email: '',
        comment: '',
        rating: 5,
        boat: boatId
      });
      setShowReviewModal(false);
      setErrors({});
      
      // Reload reviews after successful submission
      setTimeout(() => {
        loadReviews();
        setShowSuccessMessage(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting review:', error);
      
      // Handle specific backend validation errors
      if (error.message && typeof error.message === 'object') {
        // Multiple field errors
        setErrors(error.message);
      } else if (error.message) {
        // Single error message
        setErrors({ submit: error.message });
      } else {
        setErrors({ submit: 'Error submitting review. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setFormData({
      reviewer_name: '',
      email: '',
      comment: '',
      rating: 5,
      boat: boatId
    });
    setErrors({});
    setShowReviewModal(false);
  };

  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);
  const hasMoreReviews = reviews.length > 3;

  // Calculate average rating
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 0;

  // Calculate rating distribution
  const ratingDistribution = {
    5: reviews.filter(r => r.rating === 5).length,
    4: reviews.filter(r => r.rating === 4).length,
    3: reviews.filter(r => r.rating === 3).length,
    2: reviews.filter(r => r.rating === 2).length,
    1: reviews.filter(r => r.rating === 1).length
  };

  const renderStars = (rating, interactive = false, onStarClick = null) => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = index + 1;
      const isFilled = starValue <= rating;
      
      return (
        <FontAwesomeIcon
          key={index}
          icon={faStar}
          className={`star ${interactive ? 'interactive' : ''} ${isFilled ? 'filled' : 'empty'}`}
          onClick={interactive ? () => onStarClick(starValue) : undefined}
          style={{ cursor: interactive ? 'pointer' : 'default' }}
        />
      );
    });
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div className="boat-reviews">
      <div className="boat-reviews__header">
        <div className="boat-reviews__header-content">
          <h3 className="boat-reviews__title">
            <FontAwesomeIcon icon={faStar} className="boat-reviews__title-icon" />
            Customer Reviews
          </h3>
        </div>
        <button 
          className="boat-reviews__add-button"
          onClick={() => setShowReviewModal(true)}
        >
          <FontAwesomeIcon icon={faStar} />
          Write a Review
        </button>
      </div>

      {showSuccessMessage && (
        <div className="boat-reviews__success">
          <FontAwesomeIcon icon={faStar} />
          <span>Thank you for your review! It will be visible after approval.</span>
        </div>
      )}

      <div className={`boat-reviews__content ${reviews.length === 0 ? 'no-reviews' : ''}`}>
        {/* Left Column - Rating Summary */}
        <div className="boat-reviews__left-column">
          {reviews.length > 0 && (
            <div className="boat-reviews__summary">
              <div className="boat-reviews__summary-left">
                <div className="boat-reviews__average-rating">
                  <span className="boat-reviews__rating-number">{averageRating}</span>
                  <span className="boat-reviews__rating-text">Out of 5.0</span>
                </div>
              </div>
              
              <div className="boat-reviews__summary-center">
                <div className="boat-reviews__overall-rating">
                  <span>Overall Rating</span>
                  <div className="boat-reviews__stars">
                    {renderStars(Math.round(averageRating))}
                  </div>
                </div>
                <div className="boat-reviews__rating-bars">
                  {[5, 4, 3, 2, 1].map(stars => (
                    <div key={stars} className="boat-reviews__rating-bar">
                      <span className="boat-reviews__star-label">{stars} Star</span>
                      <div className="boat-reviews__bar-container">
                        <div 
                          className="boat-reviews__bar-fill"
                          style={{ 
                            width: `${reviews.length > 0 ? (ratingDistribution[stars] / reviews.length) * 100 : 0}%` 
                          }}
                        ></div>
                      </div>
                      <span className="boat-reviews__bar-count">({ratingDistribution[stars]})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Reviews List */}
        <div className="boat-reviews__right-column">
          <div className="boat-reviews__list">
            {reviews.length === 0 ? (
              <div className="boat-reviews__no-reviews">
                <FontAwesomeIcon icon={faStar} />
                <h4>No reviews yet</h4>
                <p>Be the first to share your experience with {boatName}!</p>
              </div>
            ) : (
              <>
                {displayedReviews.map((review) => (
                  <div key={review.id} className="boat-reviews__item">
                    <div className="boat-reviews__item-header">
                      <div className="boat-reviews__reviewer-info">
                        <div className="boat-reviews__reviewer-details">
                          <h4 className="boat-reviews__reviewer-name">{review.reviewer_name}</h4>
                        </div>
                      </div>
                      <div className="boat-reviews__rating">
                        {renderStars(review.rating)}
                      </div>
                    </div>
                    {review.comment && (
                      <div className="boat-reviews__comment-container">
                        <FontAwesomeIcon icon={faQuoteLeft} className="boat-reviews__quote-icon" />
                        <p className="boat-reviews__comment">{review.comment}</p>
                      </div>
                    )}
                  </div>
                ))}
                
                {hasMoreReviews && (
                  <div className="boat-reviews__load-more">
                    <button 
                      className="boat-reviews__load-more-button"
                      onClick={() => setShowAllReviews(!showAllReviews)}
                    >
                      <FontAwesomeIcon icon={showAllReviews ? faChevronUp : faChevronDown} />
                      {showAllReviews ? 'Show Less' : 'View More'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && createPortal(
        <div className="core__modal" onClick={handleResetAndClose}>
          <div className="core__modal-content" onClick={stopPropagation}>
            <div className="core__modal-header">
              <h3>Add Your Experience</h3>
              <button className="core__modal-close" onClick={handleResetAndClose}>
                <FontAwesomeIcon icon={faClose} />
              </button>
            </div>
            <div className="core__modal-body">
              <p className="core__modal-intro-text">
                Help other customers by sharing your experience with {boatName}. Your review will be visible after approval.
              </p>
              
              <div className="form">
                <div className="form-group-row">
                  <div className="form-group">
                    <input
                      type="text"
                      name="reviewer_name"
                      value={formData.reviewer_name}
                      onChange={handleChange}
                      placeholder="Your Name*"
                      className={errors.reviewer_name ? 'error' : ''}
                    />
                    {errors.reviewer_name && (
                      <span className="error-message">{errors.reviewer_name}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your Email*"
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && (
                      <span className="error-message">{errors.email}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <div className="rating-input">
                    <span>Rating*</span>
                    <div className="stars-container">
                      {renderStars(formData.rating, true, handleRatingChange)}
                      <span className="rating-text">({formData.rating} stars)</span>
                    </div>
                  </div>
                  {errors.rating && (
                    <span className="error-message">{errors.rating}</span>
                  )}
                </div>

                <div className="form-group">
                  <div className="textarea-container">
                    <FontAwesomeIcon icon={faQuoteLeft} className="textarea-icon" />
                    <textarea
                      name="comment"
                      value={formData.comment}
                      onChange={handleChange}
                      placeholder="Share your experience with this boat..."
                      className={errors.comment ? 'error' : ''}
                      rows="4"
                      maxLength="300"
                      required
                    />
                  </div>
                  <div className="character-count">
                    {formData.comment.length}/300 characters
                  </div>
                  {errors.comment && (
                    <span className="error-message">{errors.comment}</span>
                  )}
                </div>

                {errors.submit && (
                  <div className="error-message">{errors.submit}</div>
                )}

                <div className="privacy-assurance">
                  <FontAwesomeIcon icon={faShieldAlt} />
                  <span>We won't share your data.</span>
                </div>

                <div className="core__modal-buttons">
                  <button 
                    className="button" 
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.getElementById('core-app')
      )}
    </div>
  );
};

export default BoatReviews; 